import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { withCache } from '@ngneat/cashew';
import { environment } from '@y42/environment';
import { AuthNotificationsService, NotificationV2UserEvent } from '@y42/feature';
import { FeatureFlagService } from '@y42/feature-flag';
import { setGitPathHttpHeader } from '@y42/feature/git';
import { DatasourceAccounts, IntegrationSchemaModel } from '@y42/json-schema';
import { LogModel, LogsHistoryService } from '@y42/log';
import { ORG_API_URL_PARAM } from '@y42/model';
import { PushNotificationService } from '@y42/push-notification';
import { Y42FeatureFlag } from '@y42/y42-feature-flag';
import {
  catchError,
  filter,
  map,
  Observable,
  of,
  scan,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

export const SCHEMA_LOG_ID_EXPIRATION_TIMEOUT = 5 * 60 * 1000;

// export const SOURCE_CONFIG_TYPE_HEADER = 'type';
// export const SOURCE_CONFIG_CONNECTION_QUERY_PARAM = 'connection';

// export type ResourceHttpParams = Partial<
//   Pick<SourceConfigMetadataConfig, 'type'> &
//     Record<typeof SOURCE_CONFIG_CONNECTION_QUERY_PARAM, SourceConfigMetadataConfig['connection']>
//   // &  SourceConfigMetadataConfig['settings']
// >;

export interface OAuthResponse {
  redirect_to: string;
}

export interface FileTargetResponse {
  resource_uri: string;
  success: boolean;
  upload_url: string;
  storage_type: string;
  custom_headers?: Record<string, string>;
  max_chunk_size?: number;
}

export interface FetchResourceNotFoundResponse {
  response: {
    logId: string;
  };
  responseType: 'resource_log_id';
}

export interface FetchResourceResponse {
  response: IntegrationSchemaModel | DatasourceAccounts;
  responseType: 'resource';
}

@Injectable({ providedIn: 'root' })
export class TriggerService {
  constructor(
    private readonly http: HttpClient,
    private readonly pushNotificationService: PushNotificationService,
    private readonly authNotificationsService: AuthNotificationsService,
    private readonly logsService: LogsHistoryService,
    private readonly featureFlagService: FeatureFlagService<Y42FeatureFlag>,
  ) {}

  readonly legacyTriggerApiUrl = `${environment.url.trigger_api}/company/${ORG_API_URL_PARAM}`;
  readonly triggerServiceUrl = this.featureFlagService.get(Y42FeatureFlag.EnableNewTriggerApi)
    ? `${environment.url.trigger_api_new}/company/${ORG_API_URL_PARAM}`
    : this.legacyTriggerApiUrl;

  getFileTarget(orgId: number): Observable<FileTargetResponse> {
    return this.http.get<FileTargetResponse>(`${this.legacyTriggerApiUrl}/file_target?company_id=${orgId}`);
  }

  fetchResource(path: string, type: 'accounts' | 'schema', cacheKey: string, reload = false, resourceHttpParams = {}) {
    return new Observable<FetchResourceNotFoundResponse | FetchResourceResponse>((subscriber) => {
      const destroyer$$ = new Subject<void>();
      // Unsubscribe from resource fetching requests
      // if there are no more subscribers
      subscriber.add(() => destroyer$$.next());

      this.getResourceOrLogId(path, cacheKey, type, reload, resourceHttpParams)
        .pipe(
          switchMap((response) => {
            subscriber.next(response);

            if (response.responseType === 'resource') {
              subscriber.complete();
              return of(undefined);
            }

            return this.authNotificationsService
              .listen<NotificationV2UserEvent>((pushNotification) => {
                return (
                  pushNotification.location === path &&
                  pushNotification.data?.service_type === 'SERVICE_TYPE_USER_RESOURCE' &&
                  pushNotification.data?.resource_type === type
                );
              })
              .pipe(
                // no reload needed when we react to the websocket
                switchMap(() => this.getResourceOrLogId(path, cacheKey, type, false, resourceHttpParams)),
                tap((data) => {
                  if (data.responseType === 'resource_log_id') {
                    throw new Error('Invalid data received');
                  }

                  subscriber.next(data);
                  subscriber.complete();
                }),
              );
          }),
          take(1),
          catchError((error: unknown) => {
            subscriber.error(error);
            return of();
          }),
          takeUntil(destroyer$$),
        )
        .subscribe();
    });
  }

  getLogsForResource(logId: string) {
    return this.logsService.getHistoryFromChannel(`v2.${logId}`).pipe(
      switchMap((logs) => {
        return this.pushNotificationService.openChannel<LogModel>(`v2.${logId}`).pipe(
          map((evt) => evt.data),
          catchError((err: unknown) => {
            console.error(err);
            return of(null);
          }),
          filter(Boolean),
          startWith(null),
          scan((acc: LogModel[], chunk) => (chunk ? [chunk] : []).concat(acc), logs),
        );
      }),
    );
  }

  setCredentials<TResponse = unknown>(path: string, secret: Record<string, unknown>) {
    return this.http.post<TResponse>(
      `${this.legacyTriggerApiUrl}/secret`,
      { location: path, secret: secret },
      { headers: setGitPathHttpHeader(path) },
    );
  }

  // Oauth
  oAuthSignIn(
    path: string,
    clientParams?: Record<string, string>,
    urlParams?: Record<string, string>,
  ): Observable<OAuthResponse> {
    return this.http.post<OAuthResponse>(
      `${this.legacyTriggerApiUrl}/authorize`,
      { location: path, client_params: clientParams ?? undefined, url_params: urlParams ?? undefined },
      { headers: setGitPathHttpHeader(path) },
    );
  }

  private getResourceOrLogId(
    path: string,
    cacheKey: string,
    type: 'schema' | 'accounts',
    reload: boolean,
    resourceHttpParams: object,
  ): Observable<FetchResourceNotFoundResponse | FetchResourceResponse> {
    const params: Record<string, string> = this.featureFlagService.get(Y42FeatureFlag.EnableNewTriggerApi)
      ? {
          ...resourceHttpParams,
          reload: reload ? 'true' : 'false',
        }
      : reload
      ? {
          force_reload: '1',
        }
      : {};
    return this.http
      .get<IntegrationSchemaModel | DatasourceAccounts>(`${this.triggerServiceUrl}/resource/${type}`, {
        headers: setGitPathHttpHeader(path),
        params,
        context: withCache({
          key: `${type}|${path}|${cacheKey}`,
          clearCachePredicate: () => reload || !cacheKey,
        }),
      })
      .pipe(
        map(
          (response): FetchResourceResponse => ({
            response,
            responseType: 'resource',
          }),
        ),
        catchError((error: unknown) => {
          const httpError = error as HttpErrorResponse & { error: FetchResourceNotFoundResponse };
          if (httpError.status === 404 && !!httpError.error?.log_id) {
            return of<FetchResourceNotFoundResponse>({
              response: {
                logId: httpError.error.log_id,
              },
              responseType: 'resource_log_id',
            });
          }
          throw error;
        }),
      );
  }
}
